import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import configService, { 
  Country, 
  CreateCountryDto, 
  UpdateCountryDto, 
  CountryFilters,
  PaginatedResponse 
} from '@/services/configService'

interface CountryState {
  countries: Country[]
  currentCountry: Country | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filters: CountryFilters

  // Actions
  setFilters: (filters: CountryFilters) => void
  fetchCountries: (filters?: CountryFilters) => Promise<void>
  fetchCountryById: (id: string) => Promise<void>
  createCountry: (data: CreateCountryDto) => Promise<Country>
  updateCountry: (id: string, data: UpdateCountryDto) => Promise<Country>
  deleteCountry: (id: string) => Promise<void>
  clearError: () => void
  clearCurrentCountry: () => void
}

const useCountryStore = create<CountryState>()(
  devtools(
    (set, get) => ({
      countries: [],
      currentCountry: null,
      loading: false,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      filters: {
        page: 1,
        limit: 10
      },

      setFilters: (filters) => {
        set({ filters: { ...get().filters, ...filters } })
      },

      fetchCountries: async (filters = {}) => {
        try {
          set({ loading: true, error: null })
          const currentFilters = { ...get().filters, ...filters }
          const response: PaginatedResponse<Country> = await configService.countries.getAll(currentFilters)
          
          set({
            countries: response.data,
            pagination: {
              page: currentFilters.page || 1,
              limit: currentFilters.limit || 10,
              total: response.total,
              pages: Math.ceil(response.total / (currentFilters.limit || 10))
            },
            filters: currentFilters,
            loading: false
          })
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors du chargement des pays',
            loading: false
          })
        }
      },

      fetchCountryById: async (id: string) => {
        try {
          set({ loading: true, error: null })
          const country = await configService.countries.getById(id)
          set({ currentCountry: country, loading: false })
        } catch (error: any) {
          console.error('Error fetching country by ID:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors du chargement du pays'
          set({
            error: errorMessage,
            loading: false
          })
        }
      },

      createCountry: async (data: CreateCountryDto) => {
        try {
          set({ loading: true, error: null })
          const country = await configService.countries.create(data)
          
          // Add to countries list
          set((state) => ({
            countries: [country, ...state.countries],
            loading: false
          }))

          return country
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la création du pays',
            loading: false
          })
          throw error
        }
      },

      updateCountry: async (id: string, data: UpdateCountryDto) => {
        try {
          set({ loading: true, error: null })
          const country = await configService.countries.update(id, data)
          
          // Update in countries list
          set((state) => ({
            countries: state.countries.map(c => 
              c.id === id ? country : c
            ),
            currentCountry: state.currentCountry?.id === id ? country : state.currentCountry,
            loading: false
          }))

          return country
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Erreur lors de la mise à jour du pays',
            loading: false
          })
          throw error
        }
      },

      deleteCountry: async (id: string) => {
        try {
          set({ loading: true, error: null })
          await configService.countries.delete(id)
          
          // Remove from countries list
          set((state) => ({
            countries: state.countries.filter(c => c.id !== id),
            currentCountry: state.currentCountry?.id === id ? null : state.currentCountry,
            loading: false
          }))
        } catch (error: any) {
          console.error('Error deleting country:', error)
          const errorMessage = error.response?.data?.error || 
                             error.response?.data?.message || 
                             error.message || 
                             'Erreur lors de la suppression du pays'
          set({
            error: errorMessage,
            loading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
      clearCurrentCountry: () => set({ currentCountry: null })
    }),
    { name: 'CountryStore' }
  )
)

export default useCountryStore
